import React from 'react';
import './TermsService.css';

export default function TermsService() {
  return (
    <div className="terms-container">
      <header className="terms-headers">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: December 10, 2025</p>
      </header>

      <div className="terms-content">
        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Humanex platform and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you must not use our services.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            Humanex provides a comprehensive human resources management platform that includes employee data management, payroll processing, performance tracking, recruitment tools, compliance management, and related HR services. We reserve the right to modify, suspend, or discontinue any aspect of our service at any time.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. User Account and Registration</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To access our services, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          
          <h3>3.2 Account Security</h3>
          <p>
            You must immediately notify us of any unauthorized use of your account or any other breach of security. We are not liable for any loss or damage arising from your failure to protect your account information.
          </p>

          <h3>3.3 Account Eligibility</h3>
          <p>
            You must be at least 18 years old and have the legal authority to enter into these Terms on behalf of your organization. By using our services, you represent and warrant that you meet these eligibility requirements.
          </p>
        </section>

        <section className="terms-section">
          <h2>4. Acceptable Use Policy</h2>
          <p>You agree not to use our services to:</p>
          <ul>
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Transmit any harmful, threatening, abusive, or defamatory content</li>
            <li>Interfere with or disrupt the integrity or performance of our services</li>
            <li>Attempt to gain unauthorized access to our systems or networks</li>
            <li>Use automated systems or software to extract data from our platform</li>
            <li>Impersonate any person or entity or misrepresent your affiliation</li>
            <li>Upload or transmit viruses, malware, or any malicious code</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>5. Subscription and Payment Terms</h2>
          <h3>5.1 Fees</h3>
          <p>
            Access to certain features of Humanex requires payment of subscription fees. All fees are non-refundable unless otherwise stated in these Terms or required by law. Pricing is subject to change with 30 days' notice.
          </p>

          <h3>5.2 Billing</h3>
          <p>
            Subscription fees are billed in advance on a recurring basis (monthly or annually) depending on your selected plan. You authorize us to charge your payment method on file for all applicable fees.
          </p>

          <h3>5.3 Late Payment</h3>
          <p>
            If payment is not received when due, we reserve the right to suspend or terminate your access to the services until payment is made in full.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Data and Privacy</h2>
          <p>
            Your use of our services is also governed by our Privacy Policy. We collect, use, and protect your data in accordance with applicable data protection laws. You retain ownership of all data you input into our system, and you grant us a limited license to use this data solely to provide our services to you.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Intellectual Property Rights</h2>
          <h3>7.1 Our Rights</h3>
          <p>
            All content, features, and functionality of Humanex, including but not limited to software, text, graphics, logos, and trademarks, are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws.
          </p>

          <h3>7.2 Limited License</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable license to access and use our services solely for your internal business purposes in accordance with these Terms.
          </p>

          <h3>7.3 Restrictions</h3>
          <p>
            You may not copy, modify, distribute, sell, or lease any part of our services or included software, nor may you reverse engineer or attempt to extract the source code of our platform.
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Service Availability and Support</h2>
          <p>
            We strive to maintain high availability of our services but do not guarantee uninterrupted or error-free operation. We provide technical support through our designated support channels during business hours. Response times may vary based on your subscription level.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Humanex shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Humanex and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of our services, your violation of these Terms, or your violation of any third-party rights.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Termination</h2>
          <h3>11.1 Termination by You</h3>
          <p>
            You may terminate your account at any time by contacting our support team. Upon termination, you will lose access to your account and all associated data.
          </p>

          <h3>11.2 Termination by Us</h3>
          <p>
            We reserve the right to suspend or terminate your account immediately, without prior notice, if you breach these Terms or engage in any conduct that we deem inappropriate or harmful to our services or other users.
          </p>

          <h3>11.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the services will immediately cease. We may delete your data after a reasonable period, though we may retain certain information as required by law or for legitimate business purposes.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on our platform and updating the "Last Updated" date. Your continued use of our services after such modifications constitutes your acceptance of the updated Terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Humanex is registered, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or our services shall be resolved through binding arbitration in accordance with the rules of the applicable arbitration association.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Disclaimers and Warranties</h2>
          <p>
            Our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that our services will be error-free, secure, or uninterrupted.
          </p>
        </section>

        <section className="terms-section">
          <h2>15. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> support@devopstrioglobal.com</p>
            <p><strong>Phone:</strong> +1 (555) 123-4567</p>
            <p><strong>Address:</strong> 128 City Road,London, EC1V NX United Kindom</p>
          </div>
        </section>
      </div>

      {/* <footer className="terms-footer">
        <p>© 2025 Humanex. All rights reserved.</p>
      </footer> */}
    </div>
  );
}